/**
 * Domain-agnostic decision logic behind the merge drawers (EAI-1815 / EAI-1816).
 *
 * Both the opportunity merge and the lead merge ask the same four questions —
 * which record starts as master, which records take part, which value wins each
 * field, and what the resulting mutation payload is — so the answers live here
 * once. Each caller supplies its own `fields` config; nothing in this module
 * knows what an opportunity or a lead is.
 *
 * A `field` is `{ key, langKey, value(record), variables(record) }`:
 *   value     -> the string rendered in the comparison cell
 *   variables -> that record's slice of the mutation payload
 * Deriving both from the same record is what stops a displayed value and the
 * value actually merged from drifting apart.
 */

/**
 * Marks a row that was appended to a list by "Show merged records" — the value
 * is the id of the merged record it was expanded under.
 *
 * Two jobs: the collapse removes exactly these copies rather than a
 * pre-existing entry that happens to share an id, and the list tints them so an
 * archived source record never reads as a live one.
 */
export const MERGED_PREVIEW_OF = "__mergedPreviewOf";

/**
 * Index of the record that starts out as master: the most recently created.
 * Ties keep the earlier column, which is the record the user opened this from.
 *
 * @param {object[]} records
 * @param {string} createdKey - the records' creation-timestamp field.
 */
export const pickPrimaryIndex = (records, createdKey = "createdDate") =>
  (records || []).reduce(
    (best, record, index) =>
      new Date(records[best]?.[createdKey]) < new Date(record?.[createdKey]) ? index : best,
    0
  );

/**
 * Per-record flag: does this record take part in the merge? The master always
 * does — it cannot be marked "Do not merge".
 */
export const getIncludedRecords = (records, primaryIdx, excluded) =>
  (records || []).map((_, index) => index === primaryIdx || !excluded?.[index]);

/**
 * The record whose value wins for one field: the user's pick while it is still
 * taking part, otherwise the master's. The fallback keeps the Master Record
 * preview honest when a picked record is later excluded.
 */
export const resolveSelectedIndex = ({ picks, fieldKey, included, primaryIdx }) => {
  const picked = picks?.[fieldKey];
  return picked !== undefined && included?.[picked] ? picked : primaryIdx;
};

/**
 * Merges every field's winning slice into one payload object.
 *
 * @param {object[]} params.fields  - the field config described above.
 * @param {object[]} params.records
 * @param {number}   params.primaryIdx
 * @param {object}   params.excluded - record index -> true when ruled out.
 * @param {object}   params.picks    - field key -> record index.
 * @returns {object} the combined `variables` for the create mutation.
 */
export const buildFieldVariables = ({ fields, records, primaryIdx, excluded, picks }) => {
  const included = getIncludedRecords(records, primaryIdx, excluded);
  return (fields || []).reduce(
    (acc, field) => ({
      ...acc,
      ...field.variables(
        records[resolveSelectedIndex({ picks, fieldKey: field.key, included, primaryIdx })]
      ),
    }),
    {}
  );
};

/**
 * The source ids to merge, ordered duplicates-first with the master LAST.
 *
 * Every id sent is archived into the newly created record, so this is the
 * master plus the duplicates still taking part — a record marked "Do not
 * merge" is simply left out, which is what keeps it active. The ordering
 * matches the payload production has been sending, so a failed merge can't be
 * blamed on ordering.
 */
/**
 * The master and the records being merged into it, kept apart (EAI-1871).
 *
 * Both merges keep the master and update it in place, so the API takes it as its
 * own argument and 400s if it also appears in the merge list. Excluded records
 * are simply left out — that is what keeps them live.
 *
 * @returns {{masterOpportunityId: number, mergeOpportunityIds: number[]}}
 */
export const getMergeTargets = ({ records, primaryIdx, excluded }) => {
  const included = getIncludedRecords(records, primaryIdx, excluded);
  return {
    masterOpportunityId: +(records || [])[primaryIdx]?.id,
    mergeOpportunityIds: (records || [])
      .filter((_, i) => included[i] && i !== primaryIdx)
      .map(record => +record.id),
  };
};
